import { useState } from "react";
import { Loader2, Printer, FileDown, Settings2, Mail, FileStack } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { PrinterSetup } from "@/components/docs/PrinterSetup";
import { getPrinter, qzPrintRaw, qzErrorText } from "@/lib/qz";

const slugId = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

export async function downloadPdf(doc) {
  const r = await api.get(`/documents/${doc.kind}/${doc.id}/pdf`, { params: { download: true }, responseType: "blob" });
  const cd = r.headers["content-disposition"] || "";
  const name = (cd.match(/filename="([^"]+)"/) || [])[1] || `${doc.label}-${doc.number || "dokumen"}.pdf`.replace(/\//g, "-");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 30000);
}

// Opens the PDF in a (pre-opened) tab for manual printing; downloads it when popups are blocked.
export async function openPdf(doc, win) {
  const r = await api.get(`/documents/${doc.kind}/${doc.id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  const w = win && !win.closed ? win : window.open(url, "_blank");
  if (w && w === win) w.location.href = url;
  if (!w) await downloadPdf(doc);
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}

// Returns false when no printer is configured yet (caller should open setup).
export async function printRaw(doc) {
  const printer = getPrinter();
  if (!printer) return false;
  const r = await api.get(`/documents/${doc.kind}/${doc.id}/raw`);
  const d = r.data.data;
  await qzPrintRaw(printer, d.base64, `${d.title} ${d.number || ""}`);
  toast.success(`${d.title} ${d.number || ""} dikirim ke ${printer} (${d.pages} halaman)`);
  return true;
}

function useDocActions(onSetup) {
  const [busy, setBusy] = useState(null);
  const [setup, setSetupState] = useState(false);
  const setSetup = (v) => { if (v) onSetup?.(); setSetupState(v); };
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(e?.response ? apiError(e) : qzErrorText(e)); } finally { setBusy(null); }
  };
  const pdf = (doc) => run(`pdf-${doc.kind}-${doc.id}`, () => downloadPdf(doc));
  const print = (doc) => run(`raw-${doc.kind}-${doc.id}`, async () => { if (!(await printRaw(doc))) { toast.info("Pilih printer dot-matrix dulu"); setSetup(true); } });
  return { busy, pdf, print, setup, setSetup };
}

function DocRow({ doc, a }) {
  const id = `${doc.kind}-${slugId(doc.number || doc.id)}`;
  return (
    <div className="doc-row" data-testid={`doc-row-${id}`}>
      <div><strong>{doc.label}</strong><small>{doc.number}</small></div>
      <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => a.pdf(doc)} disabled={a.busy === `pdf-${doc.kind}-${doc.id}`} title="Unduh PDF (A5 landscape)" data-testid={`doc-pdf-${id}`}>
        {a.busy === `pdf-${doc.kind}-${doc.id}` ? <Loader2 size={13} className="spin" /> : <FileDown size={13} />} PDF</button>
      <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={() => a.print(doc)} disabled={a.busy === `raw-${doc.kind}-${doc.id}`} title="Print langsung ke dot-matrix" data-testid={`doc-print-${id}`}>
        {a.busy === `raw-${doc.kind}-${doc.id}` ? <Loader2 size={13} className="spin" /> : <Printer size={13} />} Print</button>
    </div>
  );
}

// Inline PDF + Print buttons for a single document.
export function DocActions({ doc, testId }) {
  const a = useDocActions();
  return (
    <div className="doc-inline" data-testid={testId}>
      <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => a.pdf(doc)} disabled={!!a.busy} title="Unduh PDF" data-testid={`${testId}-pdf`}>
        {a.busy?.startsWith("pdf") ? <Loader2 size={14} className="spin" /> : <FileDown size={14} />} PDF</button>
      <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => a.print(doc)} disabled={!!a.busy} title="Print dot-matrix" data-testid={`${testId}-print`}>
        {a.busy?.startsWith("raw") ? <Loader2 size={14} className="spin" /> : <Printer size={14} />} Print</button>
      {a.setup && <PrinterSetup onClose={() => a.setSetup(false)} />}
    </div>
  );
}

// Popover listing documents. Pass `docs` or `load` (async → docs). `extra` = [{label, icon, onClick, testId}]
export function DocMenu({ docs: initial, load, extra = [], testId, title = "Dokumen" }) {
  const [open, setOpen] = useState(false);
  const a = useDocActions(() => setOpen(false));
  const [docs, setDocs] = useState(initial || null);
  const onOpen = async (v) => {
    setOpen(v);
    if (v && load) {
      setDocs(null);
      try { setDocs(await load()); } catch (e) { toast.error(apiError(e)); setDocs([]); }
    }
  };
  return (
    <>
      <Popover open={open} onOpenChange={onOpen}>
        <PopoverTrigger asChild>
          <button className="rj-btn rj-btn-ghost rj-btn-sm" title={title} data-testid={testId}><FileStack size={14} /></button>
        </PopoverTrigger>
        <PopoverContent align="end" className="doc-menu" style={{ zIndex: 120 }} data-testid={`${testId}-menu`}>
          <div className="doc-menu-head"><b>{title}</b><small>A5 landscape · dot-matrix</small></div>
          {docs === null ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div>
            : docs.length === 0 ? <small className="fin-muted">Belum ada dokumen.</small>
            : docs.map((d) => <DocRow key={`${d.kind}-${d.id}`} doc={d} a={a} />)}
          {extra.map((x) => (
            <button key={x.testId} className="doc-menu-link" onClick={() => { setOpen(false); x.onClick(); }} data-testid={x.testId}>
              {x.icon === "mail" ? <Mail size={13} /> : null} {x.label}</button>
          ))}
          <button className="doc-menu-link" onClick={() => { setOpen(false); a.setSetup(true); }} data-testid={`${testId}-printer-setup`}><Settings2 size={13} /> Atur printer dot-matrix</button>
        </PopoverContent>
      </Popover>
      {a.setup && <PrinterSetup onClose={() => a.setSetup(false)} />}
    </>
  );
}
